<script lang="ts" setup>
import LogoComponent from '#assets/crf-logo-horizontal.svg?component';
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import { useLanguage } from '#composables/useLanguage';
import { usePermissions } from '#composables/usePermissions';
import { config } from '#config/configuration';
import { handleDocumentationAuth } from '#domain/core/documentation/documentations-links.utils';
import { C4Menu, type MenuDataType } from '@c4-links/vue3-links-components';
import { useQuery } from '@tanstack/vue-query';
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { formatDateToIso } from '../../helpers';
import { getAdminSideBarData, getSideBarData } from './sideBar.model';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  isAccountManager: { type: Boolean, default: false },
  isProjectsAuthorized: { type: Boolean, default: false },
  isProjectsAdmin: { type: Boolean, default: false },
  isInnovationsAuthorized: { type: Boolean, default: false },
  isOrganizationCreator: { type: Boolean, default: false },
  isImpersonating: { type: Boolean, default: false }
});

const emit = defineEmits<{
  toggle: [];
}>();

const { t, language } = useLanguage();
const router = useRouter();
const currentRoute = useRoute();
const permissions = usePermissions();

const { data: sideBarData, refetch } = useQuery({
  queryKey: [props.isAdmin ? 'adminSideBarData' : 'sideBarData', language],
  queryFn: () => (props.isAdmin ? getAdminSideBarData(language.value) : getSideBarData(language.value))
});

watch(
  () => props.isProjectsAdmin,
  () => {
    refetch();
  }
);
const isItemSelected = (item: MenuDataType) => {
  if (!item.path) {
    return false;
  }

  if (currentRoute.path === item.path) {
    return true;
  }

  const matchedPaths = currentRoute.matched.map((r) => r.path);
  return matchedPaths.includes(item.path);
};

const menuData = computed<MenuDataType[]>(() => {
  // Wait for permissions to be ready before computing menu data
  if (!permissions.isReady.value) {
    return [];
  }

  const routes = router.getRoutes();

  const isItemVisible = (item: MenuDataType) => {
    if (item.hidden) {
      return false;
    }

    if (!props.isInnovationsAuthorized && item.name === 'innovations') {
      return false;
    }

    if (!props.isProjectsAuthorized && item.name === 'projects') {
      return false;
    }

    const route = routes.find(({ path, name }) => item.path === path || item.name === name);

    if (!route) {
      return true;
    }

    if (route.meta.accessibleIfOneOf?.length) {
      return route.meta.accessibleIfOneOf.some((permission) => permissions[permission].value);
    }

    return (
      (route.meta.requiredPermission !== 'isAccountManager' || props.isAccountManager) &&
      (route.meta.requiredPermission !== 'isAdmin' || props.isAdmin) &&
      (route.meta.requiredPermission !== 'isOrganizationsCreator' || props.isOrganizationCreator)
    );
  };

  const items = (sideBarData.value ?? []).map((item) => ({
    ...item,
    name: props.isOpen ? t(`sideMenu.${item.name}`) : '',
    hidden: !isItemVisible(item),
    selected: isItemSelected(item),
    childSection: item.childSection?.map((child) => ({
      ...child,
      hidden: !isItemVisible(child),
      selected: isItemSelected(child),
      name: props.isOpen ? t(`sideMenu.${child.name}`) : ''
    }))
  }));

  return items.filter((item) => !item.childSection?.every((child) => child.hidden));
});

const sideBarClass = computed(() => ({
  'w-sidebar-width': props.isOpen,
  'w-sidebar-close': !props.isOpen
}));

const sideBarMargesClass = computed(() => ({
  'pl-6 mr-2 pr-4': props.isOpen,
  'pl-4 mr-1 pr-3': !props.isOpen
}));

const burgerBtnClass = computed(() => ({
  'mb-20': !props.isOpen
}));

const handleToggle = () => {
  emit('toggle');
};

const handleMenuClick = (menu: MenuDataType) => {
  if (menu.id === 'support') {
    const contactFormQueryParams = currentRoute.fullPath
      ? { previousUrl: encodeURIComponent(currentRoute.fullPath) }
      : {};

    const routeData = router.resolve({
      name: 'support',
      query: contactFormQueryParams
    });
    window.open(routeData.href, '_blank');
  }

  if (menu.id === 'documentation') {
    handleDocumentationAuth(language.value);
  }

  if (menu.id === 'innovations') {
    window.open(config.innovationsUrl, '_blank');
  }
};
</script>

<template>
  <nav :class="[sideBarClass]" class="bg-white h-screen border-r border-default-very-light py-6">
    <div class="flex flex-col h-full">
      <div :class="sideBarMargesClass">
        <div class="flex items-center space-x-4">
          <RouterLink
            v-if="isOpen"
            :to="{ name: 'performance' }"
            variant="secondary"
            class="px-4 py-1 border rounded-sm border-default-very-light flex justify-center"
          >
            <LogoComponent class="h-10" />
          </RouterLink>

          <div class="flex-1 flex justify-end">
            <ButtonIcon
              :small="true"
              :class="burgerBtnClass"
              :title="isOpen ? t('sideMenu.shortcut.close') : t('sideMenu.shortcut.open')"
              :icon="isOpen ? 'chevrons-left' : 'chevrons-right'"
              background="transparent"
              @click="() => handleToggle()"
            />
          </div>
        </div>
        <div v-if="isOpen" class="flex items-center text-default-disabled h-12 mb-2">Carrefour Links Growth</div>
      </div>
      <div :class="sideBarMargesClass" class="flex flex-col grow overflow-y-auto custom-vertical-scrollbar">
        <div class="grow">
          <C4Menu :menu-data="menuData" :is-menu-section="true" @selected-menu="handleMenuClick" />
        </div>

        <div
          v-if="config.deploy.branch || config.deploy.date || config.deploy.commitSha"
          class="mt-auto pt-6 border-t flex justify-center"
        >
          <div class="text-center text-persistent-content-main-tertiary text-xs font-mono">
            <p v-if="config.deploy.branch">{{ config.deploy.branch }}</p>
            <p v-if="config.deploy.date">{{ formatDateToIso(config.deploy.date) }}</p>
            <p v-if="config.deploy.commitSha">{{ config.deploy.commitSha }}</p>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>
